import React, { useContext, useEffect } from 'react';

import { RouteComponentProps, useParams } from 'react-router';
import { TabData, Tabs } from '../components/globals/Tabs';
import { AppPage } from '../AppContent';
import SearchResults from '../components/search/SearchResults';
import SearchBar from '../components/search/SearchBar';
import { useSearch } from '../components/search/hooks/SearchHook';
import {
  ALL_SEARCH_CATEGORIES,
  SearchCategory,
  WP_HouseTerm,
} from '../model/models';
import { useLogger } from '../services/LogService';
import { AppContext } from '../state/AppContextProvider';

interface SearchPageProps extends RouteComponentProps {}

const MIN_SEARCH_KEYWORD_LEN = 2;
const MAX_RES_MORE = 10;

const ALL_SEARCH_CATEGORIES_TABS: SearchCategory[] = [
  'all',
  ...ALL_SEARCH_CATEGORIES,
];

const SearchPage: React.FC<SearchPageProps> = (props: SearchPageProps) => {
  const logger = useLogger('SearchPage');

  const { state } = useContext(AppContext);

  const { categ, s }: any = useParams();

  const {
    keywords,
    setKeywords,
    search,
    resultsCount,
    getResults,
    isSearching,
    isSearch,
    more,
    hasMore,
    reset,
    clear,
    isCleared,
    category,
    setCategory,
  } = useSearch(MAX_RES_MORE, categ);

  const tabs: TabData[] = ALL_SEARCH_CATEGORIES_TABS.map((category) => ({
    label: `Search #${category}`,
    link: `/search/${category}`,
    component: (
      <SearchResults
        keywords={keywords}
        category={category}
        getResults={getResults}
        isSearch={isSearch}
        isSearching={isSearching}
        more={category !== 'all' ? more : undefined}
        hasMore={hasMore}
        search={search}
      />
    ),
  }));

  const fetchMore = (cat: SearchCategory) => {
    const catResultsCount = getResults(cat).items.length;
    logger.info(
      `fetchMore() resultsCount=${resultsCount}, catResultsCount=${catResultsCount}`
    );
    if (resultsCount >= 0 && catResultsCount < MAX_RES_MORE) {
      logger.info(`fetchMore() fetching more results for ${cat}`);
      // more(cat, MAX_RES_MORE - catResultsCount);
      more(cat);
    }
  };

  const onCurrentTab = (tab: TabData) => {
    const path = tab.link.split('/');
    const cat = path[path.length - 1] as SearchCategory;
    logger.info('onCurrentTab() category: ' + cat);
    setCategory(cat as SearchCategory);
    if (isSearch) {
      if (cat !== 'all') {
        fetchMore(cat);
      } else {
        ALL_SEARCH_CATEGORIES.map((category) => fetchMore(category));
      }
    }
  };

  useEffect(() => {
    if (s && state.allHouses.length > 0) {
      const urlParams = new URLSearchParams(s);

      const querySearch = urlParams.get('s');
      const queryTag = urlParams.get('t');
      const queryHouse = urlParams.get('h');
      if (querySearch !== null || queryTag !== null || queryHouse !== null) {
        const changeFilterUsers = getResults('users').changeFilterByUrl;

        if (querySearch !== null) {
          logger.info(`useEffect() search from url: '${keywords}'`);
          setKeywords(querySearch);
        }

        if ((queryHouse !== null || queryTag !== null) && changeFilterUsers) {
          let houseValue: WP_HouseTerm | null = null;
          let otherTagsValue: string | null = null;
          if (queryHouse !== null) {
            const houseId = parseInt(queryHouse);
            if (!isNaN(houseId)) {
              const find = state.allHouses.find(
                (house) => house.id === houseId
              );

              if (find) {
                houseValue = find;
              }
            }
          }

          if (queryTag !== null) {
            otherTagsValue = queryTag;
          }
          changeFilterUsers(houseValue, otherTagsValue);
        }

        search();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, state.allHouses.length]);

  const displayTabs = isSearch || category !== 'all';

  return (
    <AppPage scrollable={false}>
      <div className="px-4 md:px-14 mb-4">
        <div className="flex gap-x-4">
          <SearchBar
            keywords={keywords}
            setKeywords={setKeywords}
            minKeywordChar={MIN_SEARCH_KEYWORD_LEN}
            resultsCount={resultsCount}
            search={search}
            reset={reset}
            clear={clear}
            isCleared={isCleared}
          />
        </div>
      </div>

      {displayTabs && (
        <Tabs
          id="search-tabs"
          tabs={tabs}
          onCurrentTab={onCurrentTab}
          scrollable={true}
          {...props}
        />
      )}
    </AppPage>
  );
};

export default SearchPage;
